import React, { useId, useState } from 'react';
import { confirmPassword, forgotPassword } from '@/lib/cognito';
import { trackChangePasswordCompleted, trackChangePasswordStarted } from '@/lib/accountLinkAnalytics';
import { getPasswordValidationError, COGNITO_INVALID_PASSWORD_MESSAGE } from '@/utils/passwordValidation';
import { error } from '@/utils/logger';
import PasswordField from './PasswordField';
import {
  ERROR_TEXT_CLASS,
  FIELD_BOX_CLASS,
  FIELD_INPUT_CLASS,
  FIELD_LABEL_CLASS,
  HINT_CLASS,
  PRIMARY_BUTTON_CLASS,
  STEP_TITLE_CLASS,
  TEXT_BUTTON_CLASS,
} from './accountSettingsClasses';

interface ChangePasswordStepProps {
  signInEmail: string | null;
  onCancel: () => void;
  onChanged: () => void;
}

const HANDLED_COGNITO_CODES = new Set([
  'LimitExceededException',
  'CodeMismatchException',
  'ExpiredCodeException',
  'UserNotFoundException',
  'InvalidPasswordException',
  'UNVERIFIED_EMAIL',
]);

function cognitoCode(err: unknown): string | null {
  if (!err || typeof err !== 'object') return null;
  const { code, name } = err as { code?: unknown; name?: unknown };
  if (typeof code === 'string') return code;
  return typeof name === 'string' ? name : null;
}

export function changePasswordErrorMessage(err: unknown): string {
  switch (cognitoCode(err)) {
    case 'LimitExceededException':
      return 'We can’t send another code right now. Try again later.';
    case 'CodeMismatchException':
      return 'That code is not right. Check the email and try again.';
    case 'ExpiredCodeException':
      return 'That code expired. Send a new one.';
    case 'UserNotFoundException':
      return 'No email sign-in was found for this account.';
    case 'InvalidPasswordException':
      return COGNITO_INVALID_PASSWORD_MESSAGE;
    case 'UNVERIFIED_EMAIL':
      return 'Verify your email first, then try again.';
    default:
      return 'Something went wrong. Try again.';
  }
}

const ChangePasswordStep: React.FC<ChangePasswordStepProps> = ({ signInEmail, onCancel, onChanged }) => {
  const codeInputId = useId();
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [formError, setFormError] = useState('');
  const [isWorking, setIsWorking] = useState(false);

  const reportError = (err: unknown, context: string): void => {
    if (!HANDLED_COGNITO_CODES.has(cognitoCode(err) ?? '')) {
      error(`[ChangePasswordStep] ${context}`, err);
    }
    setFormError(changePasswordErrorMessage(err));
  };

  const sendCode = async (): Promise<void> => {
    if (!signInEmail) {
      setFormError('Your email is missing from this session. Sign in again.');
      return;
    }
    setIsWorking(true);
    setFormError('');
    try {
      await forgotPassword(signInEmail);
      trackChangePasswordStarted();
      setIsCodeSent(true);
    } catch (err) {
      reportError(err, 'Failed to send code');
    } finally {
      setIsWorking(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (!signInEmail) return;
    if (!code.trim()) {
      setFormError('Enter the code from the email.');
      return;
    }
    const validationError = getPasswordValidationError(password);
    if (validationError) {
      setFormError(validationError);
      return;
    }
    if (password !== confirmation) {
      setFormError('Passwords do not match.');
      return;
    }
    setIsWorking(true);
    setFormError('');
    try {
      await confirmPassword(signInEmail, code.trim(), password);
      trackChangePasswordCompleted();
      onChanged();
    } catch (err) {
      reportError(err, 'Failed to change password');
      setIsWorking(false);
    }
  };

  if (!isCodeSent) {
    return (
      <div className="flex w-full flex-col gap-4">
        <h3 className={STEP_TITLE_CLASS}>Change password</h3>
        <p className={HINT_CLASS}>
          We&apos;ll email a code to {signInEmail ? <strong className="text-[#1a1a1c]">{signInEmail}</strong> : 'your sign-in email'}.
          Enter it with your new password.
        </p>
        {formError && <p className={ERROR_TEXT_CLASS} role="alert">{formError}</p>}
        <div className="flex flex-col gap-3">
          <button type="button" className={PRIMARY_BUTTON_CLASS} onClick={() => { void sendCode(); }} disabled={isWorking}>
            {isWorking ? 'Sending…' : 'Send code'}
          </button>
          <button type="button" className={TEXT_BUTTON_CLASS} onClick={onCancel} disabled={isWorking}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className="flex w-full flex-col gap-4" onSubmit={handleSubmit} noValidate>
      <h3 className={STEP_TITLE_CLASS}>Change password</h3>
      <p className={HINT_CLASS}>Enter the code we emailed to {signInEmail}.</p>
      <div className="flex flex-col gap-3">
        <div className="flex w-full flex-col gap-1.5">
          <label htmlFor={codeInputId} className={FIELD_LABEL_CLASS}>Code</label>
          <div className={FIELD_BOX_CLASS}>
            <input
              id={codeInputId}
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              className={FIELD_INPUT_CLASS}
              disabled={isWorking}
            />
          </div>
        </div>
        <PasswordField
          label="New password"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          disabled={isWorking}
        />
        <PasswordField
          label="Confirm password"
          value={confirmation}
          onChange={setConfirmation}
          autoComplete="new-password"
          disabled={isWorking}
        />
      </div>
      <p className={HINT_CLASS}>
        At least 8 characters, with upper and lowercase letters, a number, and a symbol.
      </p>
      {formError && <p className={ERROR_TEXT_CLASS} role="alert">{formError}</p>}
      <div className="flex flex-col gap-3">
        <button type="submit" className={PRIMARY_BUTTON_CLASS} disabled={isWorking}>
          {isWorking ? 'Saving…' : 'Save password'}
        </button>
        <button type="button" className={TEXT_BUTTON_CLASS} onClick={() => { void sendCode(); }} disabled={isWorking}>
          Send a new code
        </button>
        <button type="button" className={TEXT_BUTTON_CLASS} onClick={onCancel} disabled={isWorking}>
          Cancel
        </button>
      </div>
    </form>
  );
};

export default ChangePasswordStep;
